import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PortalPageHeader } from "@/components/portal/PortalLayout";
import PricingMatrix from "@/components/portal/PricingMatrixV2";
import { toast } from "sonner";
import { Map, ChevronDown, ChevronRight, Save, Ban, CheckCircle2 } from "lucide-react";

/**
 * Partner Portal → Tours (Iter 47 refactor)
 * Preço cliente final + breakdown motorista/DiTours/partner por célula.
 */
export default function PartnerTours() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [drafts, setDrafts] = useState({}); // {tour_id: overrides}
  const [platformFeePct, setPlatformFeePct] = useState(15);
  const [partnerPct, setPartnerPct] = useState(0);

  const load = async () => {
    try {
      const { data: d } = await api.get("/partner/tours");
      setData(d);
      const validKeys = new Set((d.categories || []).map((c) => c.key));
      const filterCats = (obj) => {
        if (!obj || typeof obj !== "object") return obj;
        const out = {};
        for (const [k, v] of Object.entries(obj)) if (validKeys.has(k)) out[k] = v;
        return out;
      };
      const draftMap = {};
      d.tours.forEach((t) => {
        const ov = t._overrides || {};
        const cleaned = { ...ov };
        if (ov.base_prices) cleaned.base_prices = filterCats(ov.base_prices);
        if (ov.season_prices) {
          const sp = {};
          for (const [sid, entry] of Object.entries(ov.season_prices)) sp[sid] = filterCats(entry);
          cleaned.season_prices = sp;
        }
        draftMap[t.id] = cleaned;
      });
      setDrafts(draftMap);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => {
    load();
    api.get("/partner/me").then((r) => {
      const fee = r.data?.effective_ditours_fee_pct ?? r.data?.global_ditours_fee_pct ?? 15;
      setPlatformFeePct(Number(fee) || 15);
      const markup = r.data?.markup || {};
      const pct = markup.mode === "pct" ? Number(markup.default || markup.executive_markup || 0) : 0;
      setPartnerPct(pct);
    }).catch(() => {});
  }, []);

  const setDraft = (tourId, patch) => setDrafts((d) => ({ ...d, [tourId]: { ...(d[tourId] || {}), ...patch } }));
  const setDraftOverrides = (tourId, newOverrides) => setDrafts((d) => ({ ...d, [tourId]: { ...newOverrides, available: (d[tourId] || {}).available } }));

  const save = async (tourId) => {
    setSavingId(tourId);
    try {
      const payload = drafts[tourId] || {};
      await api.put(`/partner/tours/${tourId}/pricing`, payload);
      toast.success("Preços actualizados");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingId(null); }
  };

  if (!data) return <div className="p-10 text-sm text-slate-500">A carregar...</div>;

  const zoneLabel = (key) => {
    const z = data.zones.find((z) => z.key === key || z.key === (key || "").toLowerCase());
    return z?.label || key || "—";
  };
  const zoneKeyOf = (t) => (data.zones.find((z) => z.label === t.zone || z.key === (t.zone || "").toLowerCase())?.key) || (t.zone || "").toLowerCase();

  return (
    <div className="p-8 max-w-[1400px]" data-testid="partner-tours-page">
      <PortalPageHeader
        eyebrow="Catálogo"
        title="Tours"
        description="Consulte todos os tours DiTours e defina os seus próprios preços por categoria e por temporada. Deixe vazio para usar o preço base do admin. Categorias sem base admin também podem ter preço próprio definido."
      />

      <div className="space-y-4">
        {data.tours.length === 0 && (
          <div className="border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">Nenhum tour disponível.</div>
        )}
        {data.tours.map((t) => {
          const pt = (t.translations || []).find((x) => x.lang === "PT") || (t.translations || [])[0] || {};
          const cover = t.cover_image || (t.images || [])[0];
          const draft = drafts[t.id] || {};
          const isExpanded = expanded === t.id;
          const disabled = draft.available === false;
          const basePrices = {};
          (t.vehicle_pricing || []).forEach((r) => { if (r.category_key) basePrices[r.category_key] = r.price_eur; });

          return (
            <div key={t.id} className={`border ${disabled ? "border-slate-300 bg-slate-50 opacity-70" : "border-slate-200 bg-white"}`} data-testid={`ptr-tour-${t.id}`}>
              <button
                type="button"
                onClick={() => setExpanded(isExpanded ? null : t.id)}
                className="w-full flex items-center gap-4 p-4 text-left hover:bg-slate-50"
                data-testid={`ptr-tour-toggle-${t.id}`}
              >
                {cover ? (
                  <img src={cover} alt="" className="w-16 h-16 object-cover border border-slate-200" />
                ) : (
                  <div className="w-16 h-16 bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-300">
                    <Map className="w-6 h-6" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <div className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-bold">{zoneLabel(t.zone)}</div>
                    <div className="text-[10px] text-slate-500">· {t.duration_hours}h</div>
                    {disabled && <span className="text-[10px] uppercase text-red-600 font-bold flex items-center gap-1"><Ban className="w-3 h-3" /> Desactivado</span>}
                  </div>
                  <div className="font-display text-lg text-[#0a0f1e] truncate">{pt.title || t.slug}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">a partir de</div>
                  <div className="font-mono text-lg font-bold text-[#0a0f1e]">
                    €{Math.min(...Object.values(basePrices).filter((v) => v > 0), t.price_base || 999) || 0}
                  </div>
                </div>
                {isExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
              </button>

              {isExpanded && (
                <div className="border-t border-slate-100 p-5 space-y-4 bg-slate-50/30">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setDraft(t.id, { available: !disabled ? false : true })}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-widest font-bold border ${disabled ? "border-emerald-500 text-emerald-700 bg-emerald-50" : "border-red-400 text-red-600"}`}
                      data-testid={`ptr-tour-availability-${t.id}`}
                    >
                      {disabled ? <><CheckCircle2 className="w-3.5 h-3.5" /> Reactivar tour</> : <><Ban className="w-3.5 h-3.5" /> Desactivar completamente</>}
                    </button>
                    <div className="text-[11px] text-slate-500 flex-1">
                      Desactivar remove este tour do widget e sub-domínio do parceiro.
                    </div>
                  </div>

                  {!disabled && (
                    <PricingMatrix
                      basePrices={basePrices}
                      seasons={data.seasons}
                      categories={data.categories}
                      zoneKey={zoneKeyOf(t)}
                      overrides={draft}
                      onChange={(o) => setDraftOverrides(t.id, o)}
                      testIdPrefix={`ptr-tour-${t.id}`}
                    />
                  )}

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => save(t.id)}
                      disabled={savingId === t.id}
                      className="inline-flex items-center gap-2 h-10 px-5 bg-[#f59e0b] text-white font-bold text-xs uppercase tracking-wide hover:bg-[#d97706] disabled:opacity-50"
                      data-testid={`ptr-tour-save-${t.id}`}
                    >
                      <Save className="w-3.5 h-3.5" /> {savingId === t.id ? "A guardar..." : "Guardar preços"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
